import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { RuneLogo } from "@/components/RuneLogo";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { getWallet, creditsLabel } from "@/lib/wallet";
import { WALLET } from "@/constants/testIds";

// Polls the same /payments/status endpoint as the skill checkout — for a wallet_topup the
// finalize path credits the cash bucket — then reads the new balance back.
export default function WalletSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | failed
  const [balance, setBalance] = useState(null);
  const polled = useRef(false);

  useEffect(() => {
    if (polled.current) return;
    polled.current = true;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          try {
            const w = await getWallet();
            setBalance(w.spendable_cents);
          } catch (e) {
            console.warn("[wallet] balance refresh failed", e);
          }
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
      } catch (e) {
        console.warn("[wallet] status poll attempt failed; will retry", e);
      }
      if (attempts >= 8) {
        setState("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    if (!sessionId) setState("failed");
    else poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div data-testid={WALLET.successStatus} className="w-full max-w-md rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-8 text-center">
        <div className="mb-4 flex justify-center"><RuneLogo className="h-10 w-10" /></div>
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-[color:var(--rb-accent)]" />
            <h1 className="rb-display mt-4 text-xl font-semibold text-[color:var(--rb-text)]">Confirming your top-up…</h1>
            <p className="mt-2 text-sm text-[color:var(--rb-text-faint)]">Adding credits to your wallet.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-[color:var(--rb-accent)]" />
            <h1 className="rb-display mt-4 text-xl font-semibold text-[color:var(--rb-text)]">Credits added!</h1>
            {balance != null && (
              <p className="mt-2 text-sm text-[color:var(--rb-text-faint)]">You now have <span className="text-[color:var(--rb-gold)]">{creditsLabel(balance)} credits</span>.</p>
            )}
            <div className="mt-6 flex flex-col gap-2">
              <Button onClick={() => navigate("/build")} className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">Start building</Button>
              <Button variant="outline" onClick={() => navigate("/wallet")} className="rounded-xl border-[color:var(--rb-border)]">Back to wallet</Button>
            </div>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-[color:var(--rb-destructive)]" />
            <h1 className="rb-display mt-4 text-xl font-semibold text-[color:var(--rb-text)]">We couldn’t confirm your top-up</h1>
            <p className="mt-2 text-sm text-[color:var(--rb-text-faint)]">If you completed checkout, your credits may appear shortly.</p>
            <div className="mt-6">
              <Button onClick={() => navigate("/wallet")} className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">Back to wallet</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
